/**
 * The phone letting itself back in.
 *
 * The bug, measured on 28 July: put the phone down and Novamund stops, with
 * the Mac wide awake in the next room. iOS suspends a web app whose screen is
 * off; a suspended page sends nothing, so WebRTC's own liveness check stops
 * arriving and the channel dies. That much is not preventable and this file
 * does not try. What was preventable is everything after it: the desktop tore
 * its side down (`host-link.js` called `stopHosting` on close), the phone
 * announced the loss and stopped, and the banner told the owner to scan the
 * code again. Two devices that already know each other, one dead channel, and
 * the only way back was a walk to the other room.
 *
 * So the loss stays and the recovery becomes invisible. The appointment in
 * `rendezvous.js` says where to meet; this decides when to go:
 *
 *   - the channel closed, and this page is awake to notice,
 *   - or the page just became visible, which on a phone is the owner picking
 *     it up, and is the moment a channel that died an hour ago is discovered.
 *
 * It only ever tries while the page is visible. A hidden page on iOS is either
 * suspended, in which case nothing runs, or about to be, in which case a
 * half-finished handshake is worse than none. Waking up and finding the work
 * already done is the whole point.
 */

import { noteConnection } from "./connection-log";
import { connect as connectDirect } from "./direct-link";
import { readAppointment } from "./rendezvous";

/** Wait between attempts: quick at first, then patient. Milliseconds. */
export const BACKOFF_MS = [400, 1000, 2500, 5000, 10000, 15000];

/** Stop trying after this. A Mac that is off will not come back by waiting. */
export const GIVE_UP_AFTER_MS = 3 * 60 * 1000;

/** A handshake against a room the desktop may not have reached yet. */
export const REJOIN_TIMEOUT_MS = 20000;

/**
 * Watches for the moment to go back, and goes.
 *
 * Everything outside is injectable so the whole loop can be driven in a test
 * with no browser, no network and no clock to wait on.
 *
 * @param onChannel  called with the new channel; wires the app onto it
 */
export function watchForReturn({
  onChannel,
  onGaveUp = null,
  connectImpl = connectDirect,
  read = readAppointment,
  doc = typeof document !== "undefined" ? document : null,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  now = () => Date.now(),
  note = noteConnection,
  timeoutMs = REJOIN_TIMEOUT_MS,
} = {}) {
  let stopped = false;
  let trying = false;
  let attempt = 0;
  let firstTryAt = null;
  let timer = null;
  // Whether there is anything to go back to. Picking up a phone whose
  // connection never died must not open a second one on top of the working
  // one: `lost` arms this, `settled` puts it away.
  let armed = false;

  const visible = () => !doc || doc.visibilityState !== "hidden";

  const schedule = (delay) => {
    clearTimeoutImpl(timer);
    timer = setTimeoutImpl(() => {
      timer = null;
      run();
    }, delay);
  };

  async function run() {
    if (stopped || trying || !armed) return;
    if (!visible()) return; // asleep; picking the phone up will call this again

    const appointment = read();
    if (!appointment) {
      // Nothing was ever agreed: an old desktop build, or a phone that was
      // paired before this existed. The honest fallback is the QR, and the
      // banner already says so.
      note("rejoin-failed", "geen afspraak");
      stopped = true;
      onGaveUp?.("This phone has no way back to your Mac. Scan the code again.");
      return;
    }

    if (firstTryAt === null) firstTryAt = now();
    if (now() - firstTryAt > GIVE_UP_AFTER_MS) {
      note("rejoin-failed", "opgegeven");
      stopped = true;
      onGaveUp?.("Your Mac did not answer. Scan the code again to carry on.");
      return;
    }

    trying = true;
    note("rejoin-try", `poging ${attempt + 1}`);
    try {
      const opened = await connectImpl({
        role: "guest",
        room: appointment.room,
        secret: appointment.secret,
        timeoutMs,
      });
      attempt = 0;
      firstTryAt = null;
      armed = false;
      note("rejoin-ok", "");
      onChannel?.(opened);
    } catch (e) {
      // The usual reason is that the desktop has not reached the room yet, so
      // this is a retry rather than a failure. It stops being one when the
      // giving-up window runs out.
      note("rejoin-failed", e?.message ? String(e.message).slice(0, 60) : "no answer");
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      attempt += 1;
      schedule(delay);
    } finally {
      trying = false;
    }
  }

  const onVisible = () => {
    if (stopped) return;
    if (!visible()) {
      note("hidden", "");
      return;
    }
    note("visible", "");
    // Picked up. Whatever the backoff was waiting for, the answer is now.
    attempt = 0;
    clearTimeoutImpl(timer);
    timer = null;
    run();
  };

  doc?.addEventListener?.("visibilitychange", onVisible);

  return {
    /** The channel went away. Try, if this page is awake to try. */
    lost() {
      if (stopped) return;
      firstTryAt = null;
      attempt = 0;
      armed = true;
      run();
    },
    /** A connection is up again; the loop rests until the next loss. */
    settled() {
      attempt = 0;
      firstTryAt = null;
      armed = false;
      clearTimeoutImpl(timer);
      timer = null;
    },
    stop() {
      stopped = true;
      clearTimeoutImpl(timer);
      timer = null;
      doc?.removeEventListener?.("visibilitychange", onVisible);
    },
    get waiting() {
      return timer !== null;
    },
  };
}
